import { Heading, Hidden, Section, Stack } from "@origin-digital/ods-core";
import React, { ReactElement } from "react";

import {
  ComponentBaseProps,
  HeadingComponentVariants,
} from "@origin-digital/ods-types";
import styled from "styled-components";
import { ActionBarMobile } from "./ActionBarMobile";
import { Box } from "./Box";
import { HelpSupportButton } from "./HelpSupportButton";
import { NavTitle } from "./NavTitle";

export interface Premise {
  premiseName: string;
  premiseId: string;
}

export type MobileNavButtonIconType = "back" | "close";
export type MobileNavButtonType = "none" | MobileNavButtonIconType;

export interface ActionBarProps extends ComponentBaseProps {
  title?: string;
  headingComponent?: HeadingComponentVariants;
  mobileNavButton?: MobileNavButtonType;
  onClickMobileNavButton?: (
    event?: React.MouseEvent<HTMLButtonElement>
  ) => void | boolean;
  desktopVariant?: "default" | "slim";
  propertySelector?: ReactElement;
  showPropertySelector?: boolean;
  showAllProperties?: boolean;
  showServiceSelector?: boolean;
  serviceSelector?: ReactElement;
  onClickHelpSupportButton?: () => void;
}

const slimHeight = 44;
const fullHeight = 128;

const ViewContainer = styled(Box)<{
  desktopVariant: ActionBarProps["desktopVariant"];
}>`
  /* stylelint-disable-next-line media-query-no-invalid */
  @media (min-width: ${(p) => p.theme.breakpoints.lg}) {
    justify-content: space-between;
    height: ${(p) => (p.desktopVariant === "slim" ? slimHeight : fullHeight)}px;
  }
`;

const StyledHidden = styled(Hidden)`
  width: 100%;
`;

const OuterBox = styled(Box)`
  overflow: hidden;
  position: relative;
  z-index: 1000;
`;

export const ActionBar: React.FC<ActionBarProps> = ({
  title,
  headingComponent = "h1",
  mobileNavButton = "none",
  onClickMobileNavButton,
  "data-id": dataId = "action-bar",
  desktopVariant = "default",
  // @ts-ignore currently unused, for future use
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  showAllProperties = true,
  showPropertySelector = false,
  propertySelector,
  showServiceSelector = false,
  serviceSelector,
  onClickHelpSupportButton,
}) => (
  <OuterBox>
    <Box
      data-id={dataId}
      backgroundColor="white"
      display="flex"
      flexDirection="column"
      style={{ flex: 1 }}
    >
      <ViewContainer
        desktopVariant={desktopVariant}
        display="flex"
        backgroundColor="white"
        width="full"
      >
        <StyledHidden below="lg">
          <Box width="full" display="flex" height="full" alignItems="center">
            {/* eslint-disable-next-line no-negated-condition */}
            {desktopVariant !== "slim" ? (
              <Section paddingY="none" data-id="desktop-container">
                <Box display="flex" justifyContent="spaceBetween">
                  <Stack space="medium">
                    <Box
                      width="full"
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                    >
                      {title ? (
                        <Heading
                          variant="h2"
                          data-id="actionbar-heading-desktop"
                          component={headingComponent}
                        >
                          {title}
                        </Heading>
                      ) : null}
                    </Box>
                  </Stack>
                  {showPropertySelector && propertySelector}
                  {onClickHelpSupportButton && (
                    <HelpSupportButton onClick={onClickHelpSupportButton} />
                  )}
                </Box>
              </Section>
            ) : (
              <Stack>
                <NavTitle
                  mobileNavButton={mobileNavButton}
                  headingComponent={headingComponent}
                  onClickMobileNavButton={onClickMobileNavButton}
                  title={title}
                  desktopVariant={desktopVariant}
                  isDesktop={true}
                  showPropertySelector={showPropertySelector}
                  propertySelector={propertySelector}
                  onClickHelpSupportButton={onClickHelpSupportButton}
                />
              </Stack>
            )}
          </Box>
        </StyledHidden>
        <StyledHidden above="lg">
          <ActionBarMobile
            title={title}
            headingComponent={headingComponent}
            mobileNavButton={mobileNavButton}
            onClickMobileNavButton={onClickMobileNavButton}
            showPropertySelector={showPropertySelector}
            propertySelector={propertySelector}
            onClickHelpSupportButton={onClickHelpSupportButton}
          />
        </StyledHidden>
      </ViewContainer>
      {showPropertySelector && showServiceSelector && serviceSelector}
    </Box>
  </OuterBox>
);

ActionBar.displayName = "ActionBar";
